<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot></slot>
      </div>
    </template>

    <v-card data-cy="dialog-">
      <v-card-title class="d-flex justify-space-between mb-2" data-cy="dialog-">
        {{ $t('routes.node.propertiesPodDialogTitle') }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="text-body-1">
        <!-- Basic Information Section -->
        <div class="properties-section">
          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-file-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogName') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">
              {{ pod.name }}
            </v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-file-question-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogType') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">
              {{ getFileType }} <span v-if="rawAsset.filetype">({{ rawAsset.filetype }})</span>
            </v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-file-code-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogMimeType') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ mimetype }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-list-status</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogStatus') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ getFileStatus }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-lock-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogAccess') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ pod.teamonly ? $t('common.teamonly') : $t('common.public') }}</v-col>
          </v-row>
        </div>

        <v-divider class="my-3" aria-hidden="true" />

        <!-- Size Information Section -->
        <div class="properties-section">
          <v-row no-gutters class="py-1" v-if="pod.size">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-harddisk</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogAssetsSize') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ formatSize(pod.size, 2) }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-file-download-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogSourceAssetSize') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ formatSize(getAssetSize, 2) }}</v-col>
          </v-row>
        </div>

        <v-divider class="my-3" aria-hidden="true" />

        <!-- Creation Information Section -->
        <div class="properties-section">
          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-account-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogCreatedBy') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ getCreatedBy }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-calendar-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogCreatedOn') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ isoToDatetime(pod.createdat) }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="lastModifiedOriginal">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-calendar-edit</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.originalLastModified') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ toDateTime(lastModifiedOriginal) }}</v-col>
          </v-row>
        </div>

        <v-divider class="my-3" aria-hidden="true" />

        <!-- Audio/Video Information Section (conditional) -->
        <div class="properties-section">
          <v-row no-gutters class="py-1" v-if="rawAsset.audiocodec !== null && filetype !== 'image'">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-waveform</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogAudioCodec') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ rawAsset.audiocodec }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.audiochannels !== null">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-surround-sound</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogAudioChannels') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ rawAsset.audiochannels }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.audiobitrate">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-speedometer</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogAudioBitrate') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ convertBitrate(rawAsset.audiobitrate) }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.codec !== null && filetype !== 'image'">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-video-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogVideoCodec') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ rawAsset.codec }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.bitrate">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-speedometer</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogVideoBitrate') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ convertBitrate(rawAsset.bitrate) }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.aspect !== null && filetype !== 'image'">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-aspect-ratio</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogVideoAspectRatio') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ rawAsset.aspect }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.framerate">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-movie-open-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogVideoFramerate') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ roundNumber(rawAsset.framerate, 2) }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.filmdate !== null && filetype !== 'image'">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-calendar-star</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogVideoFilmDate') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ rawAsset.filmdate }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.duration">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-timer-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogDuration') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ formatDuration(rawAsset.duration) }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="rawAsset.resolution !== null">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-monitor-screenshot</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesPodDialogResolution') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ rawAsset.resolution }}</v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="cancel" data-cy="dialog--------cancel-btn">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useCommonStore } from '@/stores/CommonStore.js';
import _get from 'lodash/get';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import {
  convertBitrate,
  formatDuration,
  formatSize,
  isoToDatetime,
  roundNumber,
  toDateTime,
  truncateText,
} from '@/components/formatters.js';

export default {
  mixins: [DialogActionKeys],
  name: "PodPropertiesDialog",
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    pod: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {};
  },
  methods: {
    isoToDatetime,
    toDateTime,
    convertBitrate,
    formatDuration,
    roundNumber,
    truncateText,
    formatSize,
    cancel() {
      this.dialog = false;
    }
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    getFileType() {
      if (this.mimetype.includes('image')) {
        return this.$t('routes.pod.fileType.image');
      } else if (this.mimetype.includes('video')) {
        return this.$t('routes.pod.fileType.video');
      } else if (this.mimetype.includes('audio')) {
        return this.$t('routes.pod.fileType.audio');
      } else if (this.mimetype.includes('pdf')) {
        return this.$t('routes.pod.fileType.pdf');
      } else if (this.mimetype.includes('text')) {
        return this.$t('routes.pod.fileType.text');
      } else if (this.mimetype.includes('application')) {
        return this.$t('routes.pod.fileType.application');
      } else {
        return this.$t('routes.pod.fileType.unknown');
      }
    },
    GETPODSTATES() {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    getFileStatus() {
      switch (this.pod.state) {
        case this.GETPODSTATES.ERROR: return this.$t('routes.pod.stateError');
        case this.GETPODSTATES.PENDING: return this.$t('routes.pod.statePending');
        case this.GETPODSTATES.PROCESSING: return this.$t('routes.pod.stateProcessing');
        case this.GETPODSTATES.COMPLETE: return this.$t('routes.pod.stateComplete');
        default: return "";
      }
    },
    getAssetSize() {
      return _get(this, 'pod.asset.raw[0].size', 0);
    },
    getCreatedBy() {
      if (this.pod.jsondata?.uploaderName) {
        return `${this.$t("routes.node.externalUploader")}: ${this.pod.jsondata.uploaderName}`;
      }

      if (this.pod) {
        if (this.pod.createdby) {
          return `${this.pod.createdby.firstname} ${this.pod.createdby.lastname}`;
        }

        return this.$t('common.deletedUser');
      }

      return this.$t('routes.node.typeUnknown');
    },
    filetype() {
      const mimetype = _get(this, 'pod.asset.raw[0].mimetype', '');

      if (mimetype.includes('image')) {
        return 'image';
      } else if (mimetype.includes('video')) {
        return 'video';
      } else if (mimetype.includes('audio')) {
        return 'audio';
      } else if (mimetype.includes('pdf')) {
        return 'pdf';
      } else if (mimetype.includes('text')) {
        return 'text';
      } else if (mimetype.includes('application')) {
        return 'application';
      } else {
        return 'unknown';
      }
    },
    mimetype() {
      return _get(this, 'pod.asset.raw[0].mimetype', '---');
    },
    rawAsset() {
      return this?.pod?.asset?.raw[0] || {};
    },
    lastModifiedOriginal() {
      if (this?.pod?.jsondata?.lastModified) {
        return this?.pod?.jsondata?.lastModified;
      }

      return null;
    }
  }
};
</script>

<style scoped>
.properties-section > .v-row:nth-child(even) {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.properties-section > .v-row {
  border-radius: 4px;
  padding-left: 8px;
  padding-right: 8px;
}
</style>
